import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { ApiError } from '@mds/shared/lib/api';
import type { BookingLifecycleStatus, BookingPaymentMethod } from '@mds/shared/types/booking';

import * as bookings from '@/bookings/bookings.api';
import Button from '@/components/ui/button';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import EmptyState from '@/components/ui/empty-state';
import { controlCls } from '@/components/ui/field';
import Pager from '@/components/ui/pager';
import Select from '@mds/shared/ui/select';
import StatCard from '@/components/ui/stat-card';
import { Table, Row, Cell } from '@/components/ui/table';
import { SegmentedToggle, Toolbar } from '@/components/ui/toolbar';
import {
  PAYMENT_METHOD_OPTIONS,
  bookingSpace,
  bookingStatusLabel,
  bookingStatusTone,
  bookingWhen,
} from '@/domain/bookings';
import { stamp } from '@mds/shared/lib/dates';
import { formatCents } from '@/lib/money';
import * as payments from '@/payments/payments.api';

type View = 'ledger' | 'outstanding';

const ALL = 'All';
const PAGE_SIZE = 25;

const LEDGER_SORTS = [
  { value: 'recordedAt:desc', label: 'Most recent' },
  { value: 'recordedAt:asc', label: 'Oldest first' },
  { value: 'amountMinor:desc', label: 'Largest amount' },
  { value: 'customerName:asc', label: 'Customer A–Z' },
  { value: 'reference:asc', label: 'Reference' },
];

const OUTSTANDING_SORTS = [
  { value: 'createdAt:desc', label: 'Recently booked' },
  { value: 'balanceAmountMinor:desc', label: 'Largest balance' },
  { value: 'bookingDate:asc', label: 'Earliest date first' },
  { value: 'bookingDate:desc', label: 'Latest date first' },
  { value: 'customerName:asc', label: 'Customer A–Z' },
];

/** The two ways a booking can still owe money, as the front desk names them. */
const OUTSTANDING_STATUSES: { value: BookingLifecycleStatus | typeof ALL; label: string }[] = [
  { value: ALL, label: 'Anything owed' },
  { value: 'payment_pending', label: 'Unpaid' },
  { value: 'confirmed', label: 'Balance due' },
];

/**
 * Which bookings' payments the ledger shows. "Live bookings" is the default and
 * leaves cancelled ones out; asking for them by name is how a kept deposit is
 * found again.
 */
const LEDGER_STATUSES: { value: BookingLifecycleStatus | typeof ALL; label: string }[] = [
  { value: ALL, label: 'Live bookings' },
  { value: 'payment_pending', label: 'Unpaid bookings' },
  { value: 'confirmed', label: 'Confirmed bookings' },
  { value: 'cancelled', label: 'Cancelled bookings' },
];

/** The server's search filter stops here. */
const SEARCH_MAX = 160;

/**
 * The manual-payment ledger, and the bookings still owing money.
 *
 * Payments are read-only here: they are taken against a booking, from the
 * booking itself on Spaces or Bookings. Every row is immutable: payments are
 * append-only on the server, so there is no edit, no delete and no refund.
 * Searching, filtering, paging and the summary figures are the server's,
 * counted over the same filter as the rows.
 *
 * The Outstanding view is a different grain — one row per booking with a
 * balance, not one per payment — because a ledger of payments cannot show a
 * booking nobody has paid anything on yet. It reads the bookings list with the
 * `balance` filter (`backend/docs/bookings.md`), and the three figures above
 * it are the server's summary over the whole filtered result.
 *
 * Payments on cancelled bookings are left out of the ledger table unless they
 * are asked for by status. A ledger row carries its booking's status, so the
 * page needs no read per booking; the server's summary already counts only
 * live bookings as outstanding, so a page can show fewer rows than it was sent
 * without the figures moving.
 */
export default function PaymentsPage() {
  const navigate = useNavigate();
  const [view, setView] = useState<View>('ledger');

  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);
  const [method, setMethod] = useState(ALL);
  const [bookingStatus, setBookingStatus] = useState<string>(ALL);
  const [sort, setSort] = useState('recordedAt:desc');

  const [owedPage, setOwedPage] = useState(1);
  const [owedStatus, setOwedStatus] = useState<string>(ALL);
  const [owedSort, setOwedSort] = useState('createdAt:desc');

  // Typing is not a page of results each keystroke.
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
      setOwedPage(1);
    }, 300);
    return () => clearTimeout(id);
  }, [q]);

  const [sortKey, direction] = sort.split(':') as [
    'recordedAt' | 'amountMinor' | 'reference' | 'customerName',
    'asc' | 'desc',
  ];
  const [owedSortKey, owedDirection] = owedSort.split(':') as [
    'createdAt' | 'balanceAmountMinor' | 'bookingDate' | 'customerName',
    'asc' | 'desc',
  ];

  const list = useQuery({
    queryKey: ['payments', { page, search, method, bookingStatus, sort }],
    queryFn: () =>
      payments.listPayments({
        page,
        limit: PAGE_SIZE,
        search: search || undefined,
        method: method === ALL ? undefined : (method as BookingPaymentMethod),
        bookingStatus:
          bookingStatus === ALL ? undefined : (bookingStatus as BookingLifecycleStatus),
        sort: sortKey,
        direction,
      }),
    enabled: view === 'ledger',
  });

  const owed = useQuery({
    queryKey: ['bookings', { balance: 'outstanding', page: owedPage, search, owedStatus, owedSort }],
    queryFn: () =>
      bookings.listBookings({
        page: owedPage,
        limit: PAGE_SIZE,
        balance: 'outstanding',
        status: owedStatus === ALL ? undefined : (owedStatus as BookingLifecycleStatus),
        search: search || undefined,
        sort: owedSortKey,
        direction: owedDirection,
      }),
    enabled: view === 'outstanding',
  });

  const rows = useMemo(() => list.data?.data ?? [], [list.data]);
  const pagination = list.data?.pagination;
  const summary = list.data?.summary;

  // Payments on cancelled bookings are left out unless cancelled bookings were
  // asked for. A row without a status stays in: hiding a real payment is worse
  // than showing one on a cancelled booking.
  const shown = useMemo(
    () =>
      bookingStatus === ALL ? rows.filter((p) => p.booking.status !== 'cancelled') : rows,
    [rows, bookingStatus],
  );
  const hidden = rows.length - shown.length;

  const owedRows = owed.data?.data ?? [];
  const owedSummary = owed.data?.summary;

  return (
    <div className="flex flex-col gap-6">
      {view === 'ledger' ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <StatCard
            label="Collected (filtered)"
            value={summary ? formatCents(summary.collectedAmountMinor) : '—'}
            note={
              summary
                ? `${summary.paymentCount} ${summary.paymentCount === 1 ? 'payment' : 'payments'}`
                : 'Loading'
            }
            density="compact"
          />
          <StatCard
            label="Outstanding"
            value={summary ? formatCents(summary.outstandingAmountMinor) : '—'}
            note={
              !summary
                ? 'Loading'
                : summary.outstandingBookingCount === 0
                  ? 'Everything settled'
                  : `${summary.outstandingBookingCount} ${
                      summary.outstandingBookingCount === 1 ? 'booking' : 'bookings'
                    } to collect`
            }
            density="compact"
            onClick={() => setView('outstanding')}
            actionLabel="Show the bookings still owing money"
          />
          {/* Deposits are non-refundable and no refund action exists, so the third
              figure is what has actually been banked rather than a refund total. */}
          <StatCard
            label="Rows on this page"
            value={String(shown.length)}
            note={
              !pagination
                ? 'Loading'
                : hidden > 0
                  ? `${hidden} on cancelled bookings hidden`
                  : `of ${pagination.total} matching`
            }
            density="compact"
          />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {/* The totals are the server's over the whole filtered result. Until it
              sends them, a dash — a sum of 25 rows would be wrong and look right. */}
          <StatCard
            label="Outstanding (filtered)"
            value={owedSummary ? formatCents(owedSummary.outstandingAmountMinor) : '—'}
            note={
              owedSummary
                ? `${owedSummary.outstandingBookingCount} ${
                    owedSummary.outstandingBookingCount === 1 ? 'booking' : 'bookings'
                  } to collect`
                : owed.isPending
                  ? 'Loading'
                  : 'Awaiting the server total'
            }
            tag={owed.isSuccess && !owedSummary ? 'pending' : undefined}
            density="compact"
          />
          <StatCard
            label="Unpaid"
            value={owedSummary ? String(owedSummary.unpaidBookingCount) : '—'}
            note="Bookings with nothing paid yet"
            density="compact"
          />
          <StatCard
            label="Balance due"
            value={owedSummary ? String(owedSummary.depositDueBookingCount) : '—'}
            note="Confirmed on a deposit, balance still owed"
            density="compact"
          />
        </div>
      )}

      <Card
        title={view === 'ledger' ? 'Ledger' : 'Outstanding'}
        action={
          <Toolbar>
            <input
              placeholder={
                view === 'ledger' ? 'Payment ref, booking ref or customer' : 'Booking ref or customer'
              }
              value={q}
              maxLength={SEARCH_MAX}
              onChange={(e) => setQ(e.target.value)}
              aria-label={view === 'ledger' ? 'Search payments' : 'Search outstanding bookings'}
              className={controlCls + ' w-[280px]'}
            />
            {view === 'ledger' ? (
              <>
                <Select
                  value={method}
                  onChange={(v) => {
                    setMethod(v);
                    setPage(1);
                  }}
                  options={[{ value: ALL, label: 'All methods' }, ...PAYMENT_METHOD_OPTIONS]}
                  aria-label="Filter by method"
                />
                <Select
                  value={bookingStatus}
                  onChange={(v) => {
                    setBookingStatus(v);
                    setPage(1);
                  }}
                  options={LEDGER_STATUSES}
                  aria-label="Filter by booking status"
                />
                <Select
                  value={sort}
                  onChange={(v) => {
                    setSort(v);
                    setPage(1);
                  }}
                  options={LEDGER_SORTS}
                  aria-label="Sort payments"
                />
              </>
            ) : (
              <>
                <Select
                  value={owedStatus}
                  onChange={(v) => {
                    setOwedStatus(v);
                    setOwedPage(1);
                  }}
                  options={OUTSTANDING_STATUSES}
                  aria-label="Filter by what is owed"
                />
                <Select
                  value={owedSort}
                  onChange={(v) => {
                    setOwedSort(v);
                    setOwedPage(1);
                  }}
                  options={OUTSTANDING_SORTS}
                  aria-label="Sort outstanding bookings"
                />
              </>
            )}
            <SegmentedToggle
              value={view}
              options={['ledger', 'outstanding'] as const}
              onChange={setView}
            />
          </Toolbar>
        }
      >
        {view === 'outstanding' ? (
          owed.isPending ? (
            <p className="m-0 text-base text-ink-muted">Finding the bookings still owing…</p>
          ) : owed.isError ? (
            <EmptyState
              title="Could not load the outstanding bookings"
              hint={
                owed.error instanceof ApiError
                  ? owed.error.message
                  : 'The server could not be reached.'
              }
            />
          ) : owedRows.length === 0 ? (
            <EmptyState
              title="Nothing is owed"
              hint={
                search || owedStatus !== ALL
                  ? 'No booking with a balance matches these filters.'
                  : 'Every booking is settled.'
              }
            />
          ) : (
            <>
              {/* Each row opens the booking on Bookings, where money is taken. */}
              <Table
                head={['Ref', 'Customer', 'Space', 'When', 'Total', 'Paid', 'Balance', 'Status']}
              >
                {owedRows.map((b) => (
                  <Row key={b.id} onClick={() => navigate(`/bookings?bookingId=${b.id}`)}>
                    <Cell className="font-mono text-md text-ink-muted">{b.reference}</Cell>
                    <Cell className="font-semibold">{b.customer.name}</Cell>
                    <Cell>{bookingSpace(b)}</Cell>
                    <Cell className="font-mono text-md">{bookingWhen(b)}</Cell>
                    <Cell className="tabular-nums">{formatCents(b.payment.totalAmountMinor)}</Cell>
                    <Cell className="text-ink-muted tabular-nums">
                      {b.payment.paidAmountMinor > 0 ? formatCents(b.payment.paidAmountMinor) : '—'}
                    </Cell>
                    <Cell className="font-semibold text-warn-text tabular-nums">
                      {formatCents(b.payment.balanceAmountMinor)}
                    </Cell>
                    <Cell>
                      <Chip className={'border ' + bookingStatusTone(b.status)}>
                        {bookingStatusLabel(b.status)}
                      </Chip>
                    </Cell>
                  </Row>
                ))}
              </Table>
              <Pager pagination={owed.data?.pagination} onPage={setOwedPage} />
            </>
          )
        ) : list.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading the ledger…</p>
        ) : list.isError ? (
          <EmptyState
            title="Could not load the ledger"
            hint={
              list.error instanceof ApiError
                ? list.error.message
                : 'The server could not be reached.'
            }
          />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No payments match"
            hint="Try a different method or status, or clear the search."
          />
        ) : (
          <>
            {/* Paging stays below even when every row here was on a cancelled
                booking, or the pages after this one could not be reached. */}
            {shown.length === 0 ? (
              <EmptyState
                title="Nothing to show on this page"
                hint="Every payment on this page is on a cancelled booking."
              />
            ) : (
              <Table
                head={[
                  'Ref',
                  'Customer',
                  'Booking',
                  'Recorded',
                  'Method',
                  'Amount',
                  'Balance left',
                ]}
              >
                {shown.map((p) => (
                  <Row key={p.id}>
                    <Cell className="font-mono text-md text-ink-muted">{p.reference}</Cell>
                    <Cell className="font-semibold">{p.customer.name}</Cell>
                    <Cell>
                      <div className="flex flex-col">
                        <span className="font-mono text-md">{p.booking.reference}</span>
                        <span className="text-md text-ink-muted">{p.resource.name}</span>
                      </div>
                    </Cell>
                    <Cell className="font-mono text-md">{stamp(p.recordedAt)}</Cell>
                    <Cell>
                      <Chip>{p.methodLabel}</Chip>
                    </Cell>
                    <Cell className="font-semibold tabular-nums">{formatCents(p.amountMinor)}</Cell>
                    {/* The balance as it stood after this row, which is what the
                      receipt in the customer's hand says. */}
                    <Cell
                      className={
                        'tabular-nums ' +
                        (p.balanceAfterAmountMinor > 0
                          ? 'font-semibold text-warn-text'
                          : 'text-ink-muted')
                      }
                    >
                      {p.balanceAfterAmountMinor > 0
                        ? formatCents(p.balanceAfterAmountMinor)
                        : 'Settled'}
                    </Cell>
                  </Row>
                ))}
              </Table>
            )}

            {pagination && pagination.totalPages > 1 && (
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <span className="font-mono text-md text-ink-muted">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={pagination.page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={!pagination.hasMore}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
